<template>
  <div>
    <ReportMobileFrame
      v-if="$vuetify.breakpoint.smAndDown"
      :loading="isLoading"
      :empty="!isLoading && activeList.length === 0"
      empty-text="No sales data for the selected range."
    >
      <template #filters>
        <div class="report-dates-grid">
          <MobileDateField label="Start Date" :value="dates[0]" @input="onMobileDateChange(0, $event)" />
          <MobileDateField label="End Date" align="right" :value="dates[1] || dates[0]" @input="onMobileDateChange(1, $event)" />
        </div>
        <v-select
          v-if="stores.length > 1"
          :items="stores" item-text="name" item-value="id"
          v-model="selectedStoreId"
          label="Store" outlined dense hide-details class="mt-2"
          @change="load"
        />
        <v-text-field
          v-model="productSearch"
          label="Search for product"
          prepend-inner-icon="mdi-magnify"
          clearable outlined dense hide-details class="mt-2"
        />
        <div class="transaction-total mt-2">
          <span>Total transactions</span>
          <strong>{{ transactionCount.toLocaleString() }}</strong>
        </div>
        <div class="report-chips">
          <span class="report-chips__label">View</span>
          <div class="report-chip-row">
            <button type="button" class="report-chip"
              :class="{ 'report-chip--active': mobileTab === 'best' }"
              @click="mobileTab = 'best'">Best Sellers</button>
            <button type="button" class="report-chip"
              :class="{ 'report-chip--active': mobileTab === 'worst' }"
              @click="mobileTab = 'worst'">Worst Sellers</button>
          </div>
        </div>
      </template>

      <ReportListRow v-for="(item, idx) in activeList" :key="item.productName + '-' + idx">
        <template #leading>
          <div class="report-rank">#{{ idx + 1 }}</div>
        </template>
        <template #title>
          {{ item.productName }}
          <v-chip v-if="item.isDeleted" x-small color="grey" dark>Deleted</v-chip>
        </template>
        <template #subtitle>{{ item.variants.length }} historical {{ item.variants.length === 1 ? 'label' : 'labels' }}</template>
        <template #trailing>{{ item.quantity }} sold</template>
      </ReportListRow>
    </ReportMobileFrame>

    <template v-else>
    <v-row justify="center">
      <v-toolbar flat class="toolbar-container" height='100'>
        <div class="toolbar-content">
          <div class="toolbar-datepicker">
            <DateRangePicker :initialDates="dates" @blur="updateDates"  />
          </div>

          <v-select :items="stores" item-text="name" v-model="selectedStoreId" item-value="id" />

          <v-text-field
            v-model="productSearch"
            label="Search for product"
            prepend-inner-icon="mdi-magnify"
            clearable hide-details
          />

          <v-spacer></v-spacer>

          <v-btn class="search-btn" type="button" color="success" v-on:click="load">
            SEARCH
          </v-btn>
        </div>
      </v-toolbar>
    </v-row>

    <v-row>
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text>
            <div class="text-subtitle-2">Total transactions</div>
            <div class="text-h4">{{ transactionCount.toLocaleString() }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="titles">
      <v-col>
        <h2>Best Sellers</h2>
      </v-col>
       <v-col>
        <h2>Worst Sellers</h2>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <div>
          <BestSellersPieChart :data="filteredBestSellers"/>
        </div>
      </v-col>
       <v-col>
        <div>
          <BestSellersPieChart :data="filteredWorstSellers"/>
        </div>
      </v-col>
    </v-row>

    <v-row>
        <v-col>
          <v-data-table 
            :headers="headers" :items="filteredBestSellers" 
            :loading="isLoading"
            show-expand single-expand
            item-key="productName" class="elevation-1 mt-5 flex-grow-1">
            <template v-slot:[`item.productName`]="{ item }">
              {{ item.productName }}
              <v-chip v-if="item.isDeleted" x-small color="grey" dark>Deleted</v-chip>
            </template>
            <template v-slot:expanded-item="{ item }">
              <td :colspan="headers.length + 1" class="pa-0">
                <v-simple-table dense>
                  <tbody>
                    <tr v-for="variant in item.variants" :key="variant.name">
                      <td>
                        {{ variant.name }}
                        <v-chip v-if="variant.isDeleted" x-small color="grey" dark>Deleted</v-chip>
                      </td>
                      <td class="text-right">{{ variant.quantity }} sold</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </td>
            </template>
          </v-data-table>
        </v-col>
        <v-col>
          <v-data-table
            :headers="headers" :items="filteredWorstSellers"
            :loading="isLoading"
            show-expand single-expand
            item-key="productName" class="elevation-1 mt-5 flex-grow-1">
            <template v-slot:[`item.productName`]="{ item }">
              {{ item.productName }}
              <v-chip v-if="item.isDeleted" x-small color="grey" dark>Deleted</v-chip>
            </template>
            <template v-slot:expanded-item="{ item }">
              <td :colspan="headers.length + 1" class="pa-0">
                <v-simple-table dense>
                  <tbody>
                    <tr v-for="variant in item.variants" :key="variant.name">
                      <td>
                        {{ variant.name }}
                        <v-chip v-if="variant.isDeleted" x-small color="grey" dark>Deleted</v-chip>
                      </td>
                      <td class="text-right">{{ variant.quantity }} sold</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </td>
            </template>
          </v-data-table>
        </v-col>
    </v-row>
    </template>

  </div>
</template>

<script>
import DateRangePicker from '@/components/DateRangePicker.vue';
import BestSellersPieChart from '@/components/charts/BestSellersPieChart.vue';
import ReportMobileFrame from '@/components/reports/ReportMobileFrame.vue';
import ReportListRow from '@/components/reports/ReportListRow.vue';
import MobileDateField from '@/components/reports/MobileDateField.vue';
import { reportService } from '@/services';
import moment from 'moment'
import { userService } from '@/services';
import router from "../../router";

export default {

  name: 'BestSellers',
  components: {
    DateRangePicker,
    BestSellersPieChart,
    ReportMobileFrame,
    ReportListRow,
    MobileDateField
  },
  data: vm => ({
    // dates: [new Date().toISOString().substr(0, 10)],
    dates: [moment().add(-12, 'M').toISOString().substr(0, 10), moment().toISOString().substr(0, 10)],
    mobileTab: 'best',

    stores: [],
    selectedStoreId: null,
    productSearch: '',
    transactionCount: 0,

    isLoading: false,
    headers: [
      { text: 'Product', align: 'start', sortable: true, filterable: true, value: 'productName' },
      { text: 'Quantity', align: 'start', sortable: true, filterable: true, value: 'quantity' },
    ],
    bestSellers: [],
    worstSellers: [],
  }),
  async created () {
    this.stores = await reportService.getStores();
    this.stores.unshift({
      id: null,
      name: 'All'
    });

    this.load();

    if (userService.isStaff()) {
      router.push('/products');
    }
  },
  methods: {
    updateDates(newDates) {
      this.dates = newDates;
    },
    async load() {
      var startDate = this.dates[0];
      var endDate = this.dates[0];
      if (this.dates.length > 1)
        endDate = this.dates[1];

      this.isLoading = true;
      try {
        const [bestSellers, worstSellers, takings] = await Promise.all([
          reportService.getBestSellersReport(startDate, endDate, this.selectedStoreId),
          reportService.getWorstSellersReport(startDate, endDate, this.selectedStoreId),
          reportService.getTakingsReport(startDate, endDate, this.selectedStoreId)
        ]);
        this.bestSellers = bestSellers;
        this.worstSellers = worstSellers;
        this.transactionCount = takings.reduce((total, day) => total + day.noOfSales, 0);
      } finally {
        this.isLoading = false;
      }
    },
    onMobileDateChange(index, value) {
      if (!value) return;
      const next = this.dates.slice();
      while (next.length <= index) next.push(value);
      next[index] = value;
      if (index === 0) next[1] = value;
      this.dates = next;
      this.load();
    },
    filterProducts(items) {
      const search = (this.productSearch || '').trim().toLowerCase();
      return items.filter(item => (item.productName || '').toLowerCase().includes(search));
    }
  },
  computed: {
    filteredBestSellers() {
      return this.filterProducts(this.bestSellers);
    },
    filteredWorstSellers() {
      return this.filterProducts(this.worstSellers);
    },
    activeList() {
      return this.mobileTab === 'best' ? this.filteredBestSellers : this.filteredWorstSellers;
    }
  }
}
</script>

<style lang="scss" scoped>
  .toolbar-container {
    background: #FFF !important;
    padding: 10px;
    height: auto;
    border-radius: 8px;

    border: 1px solid lighten(#7B5CD8, 30);
  }

  .toolbar-content {
    display: flex;
    flex-direction: row;
    width: 100%;

    .toolbar-datepicker {
      width: 250px;
    }
  }

  .sub-total {
    ul {
      width: 100%;
      list-style-type: none;
      margin: 0;
      padding: 10px 10px;
    }
  
  }

  .titles {
    h2 {
      text-align: center;
      padding: 20px;
      border-bottom: 2px solid #7b5cd8;
    }
  }

  .report-rank {
    align-items: center;
    background: #e5e7eb;
    border-radius: 50%;
    color: var(--app-text);
    display: flex;
    font-size: 13px;
    font-weight: 700;
    height: 38px;
    justify-content: center;
    width: 38px;
  }

  .transaction-total {
    align-items: center;
    background: #f8fafc;
    border: 1px solid var(--app-border);
    border-radius: var(--app-radius);
    display: flex;
    justify-content: space-between;
    padding: 8px 12px;
  }
</style>
